/**
 * `createOfflinePersister` — IndexedDB-backed cache persister for the
 * Mangrove POS apps (ADR-0056 Layer 1).
 *
 * What this gives every PWA app that uses it:
 *
 *   1. Stale-while-revalidate reads — every `useQuery` returns the cached
 *      response instantly on reload, then refetches in the background.
 *      Sub-30ms perceived click latency on cached paths.
 *   2. Offline reads — same cache serves queries when `navigator.onLine`
 *      is false. UI keeps rendering with whatever was last known.
 *   3. Paused-mutation queue — TanStack Query's `networkMode: 'offlineFirst'`
 *      pauses mutations issued while offline. `resumePausedMutations()`
 *      fires automatically on the `online` event AND on cache rehydration
 *      (see `PosTrpcProvider` `onSuccess`), so writes drain reliably.
 *
 * Storage shape: one IndexedDB database per app (`dbName`), one object
 * store (`query-cache`), one key (`mangrove.tanstack-query`) holding the
 * full serialized cache. The persister is throttled internally by
 * TanStack — we don't write on every state change.
 *
 * **Do not use in customer-terminal** — that app is a guest-shared
 * device; persisting cache across orders leaks state between diners.
 *
 * Implementation deliberately leans on `@tanstack/react-query-persist-client`
 * and `idb-keyval` (~600B). We don't ship a Workbox-style fetch
 * interceptor — TanStack Query's own cache layer already does the right
 * thing once it has a persistent home.
 *
 * #715 (RS Linkers addendum E10, "no offline PII by default"): an app may
 * pass a POLICY saying which queries may be kept on the device. Everything
 * else stays in memory for the session and is never written to IndexedDB;
 * paused mutations (what someone typed while offline) are kept only when the
 * policy keeps everything. The same filter runs on restore, so a full copy
 * written before the policy (or while a workspace allowed it) is dropped on
 * the next load rather than served. With no policy, behaviour is unchanged.
 */
import type { Persister, PersistedClient } from '@tanstack/react-query-persist-client';
import { createStore, del, get, set } from 'idb-keyval';

const CACHE_KEY = 'mangrove.tanstack-query';
const OBJECT_STORE = 'query-cache';

/** Which queries may be kept on the device (#715, E10). */
export interface OfflineCachePolicy {
  /** Keep this query in the copy on the device? */
  keep: (queryKey: readonly unknown[]) => boolean;
  /**
   * Keep everything, as without a policy (the workspace allowed a full copy).
   * Read at every write, so turning it on or off takes effect on the next one.
   */
  keepAll?: () => boolean;
}

/**
 * The tRPC procedure a query key belongs to, as `router.procedure`: tRPC keys
 * start with the path segments (`[['auth', 'getCurrentUser'], {...}]`).
 * Anything else returns null, so an unknown key is never kept by an allowlist.
 */
export function procedurePath(queryKey: readonly unknown[]): string | null {
  const head = queryKey[0];
  if (!Array.isArray(head) || head.length === 0) return null;
  if (!head.every((part): part is string => typeof part === 'string')) return null;
  return head.join('.');
}

/** A policy that keeps exactly the listed procedures (and everything when `keepAll` says so). */
export function allowlistPolicy(
  procedures: readonly string[],
  keepAll?: () => boolean,
): OfflineCachePolicy {
  const allowed = new Set(procedures);
  return {
    keep: (queryKey) => {
      const path = procedurePath(queryKey);
      return path !== null && allowed.has(path);
    },
    ...(keepAll ? { keepAll } : {}),
  };
}

/** The part of a persisted cache a policy lets stay on the device. Pure. */
export function filterPersistedClient(
  client: PersistedClient,
  policy: OfflineCachePolicy | undefined,
): PersistedClient {
  if (!policy || policy.keepAll?.() === true) return client;
  return {
    ...client,
    clientState: {
      ...client.clientState,
      queries: client.clientState.queries.filter((q) => policy.keep(q.queryKey)),
      // What someone typed while offline is theirs to lose on a reload, not
      // the device's to keep.
      mutations: [],
    },
  };
}

/**
 * Returns a Persister that stores the TanStack Query cache in IndexedDB.
 * Returns a no-op persister on environments without `indexedDB` (SSR /
 * very old browsers) so callers don't need to branch.
 */
export function createOfflinePersister(dbName: string, policy?: OfflineCachePolicy): Persister {
  if (typeof indexedDB === 'undefined') {
    return {
      persistClient: () => Promise.resolve(),
      restoreClient: () => Promise.resolve(undefined),
      removeClient: () => Promise.resolve(),
    };
  }

  const store = createStore(dbName, OBJECT_STORE);

  return {
    persistClient: async (client: PersistedClient): Promise<void> => {
      try {
        await set(CACHE_KEY, filterPersistedClient(client, policy), store);
      } catch {
        // Quota or IDB lock — drop the write rather than crashing the
        // app. The next persist tick will retry.
      }
    },
    restoreClient: async (): Promise<PersistedClient | undefined> => {
      try {
        const saved = await get<PersistedClient>(CACHE_KEY, store);
        return saved ? filterPersistedClient(saved, policy) : undefined;
      } catch {
        return undefined;
      }
    },
    removeClient: async (): Promise<void> => {
      try {
        await del(CACHE_KEY, store);
      } catch {
        // ignore
      }
    },
  };
}
